import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '../../../../../components';
import { BottomSheetChangeBreakpoint } from '../../../../../components/Modal/Modal';
import { useSessionContext } from '../../../../../contexts/SessionContext';
import { useAppContext } from '../../../../../hooks/AppContext';
import { ExtendedListing } from '../../../../../models/Listing';
import {
  ListingDocument,
  ListingsDocument,
  Organization,
  useShareListingMutation,
} from '../../../../../types/api.graphql';
import { EmailRegex } from '../../../../../utils/RouteHelper';
import { useListingsFiltersQueryVariables } from '../../hooks/useListings';
import { InitialStep, ShareByOrganizationStep, ShareWithEmailStep } from './components';
import { CancelButton, ShareButton, StyledFooter } from './ShareDialog.styles';

export enum ShareStep {
  INITIAL_STEP,
  SHARE_WITH_ORG_STEP_1,
  SHARE_WITH_ORG_STEP_2,
  SHARE_WITH_ORG_ADD_CONNECTION,
  SHARE_WITH_EMAIL,
}

interface ShareDialogProps {
  listing: ExtendedListing;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  /**
   * If true, the VDP information will be refetched after the share is done.
   * This will allow the user to get the new information for the activity panel, among other stuff.
   */
  shouldRefetchListingDetails?: boolean;
}

export type ExtendedOrg = Organization & { isConnection?: boolean };

const CLOSE_MODAL_TIMEOUT = 300;

const ShareDialog = ({ listing, isOpen, setIsOpen, shouldRefetchListingDetails }: ShareDialogProps) => {
  // Dependencies
  const { t } = useTranslation();
  const { showAlert, showError } = useAppContext();
  const [shareListingMutation, { loading }] = useShareListingMutation({});
  const { getFiltersQueryVariables } = useListingsFiltersQueryVariables();
  const { me } = useSessionContext();

  // States
  const [step, setStep] = useState(ShareStep.INITIAL_STEP);
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState<ExtendedOrg | undefined>(undefined);
  const [organizationFrom, setOrganizationFrom] = useState<Organization | undefined>(undefined);

  // Derived States
  const isShareByOrgStep = [
    ShareStep.SHARE_WITH_ORG_STEP_1,
    ShareStep.SHARE_WITH_ORG_STEP_2,
    ShareStep.SHARE_WITH_ORG_ADD_CONNECTION,
  ].includes(step);
  const showFooterButtons = [ShareStep.SHARE_WITH_EMAIL, ShareStep.SHARE_WITH_ORG_STEP_2].includes(step);
  const disableSubmitButton =
    loading ||
    (step === ShareStep.SHARE_WITH_EMAIL && (email.length === 0 || !EmailRegex.test(email.trim()))) ||
    (isShareByOrgStep && !organization);

  const renderTitle = useCallback(
    () =>
      step === ShareStep.INITIAL_STEP
        ? t('vdp.shareModal.initialStepTitle')
        : step === ShareStep.SHARE_WITH_ORG_ADD_CONNECTION
        ? t('vdp.shareModal.addConnection')
        : t('vdp.shareModal.shareStepTitle'),
    [step, t],
  );

  // Effects
  useEffect(() => {
    if (listing?.receivingOrganization) {
      setOrganizationFrom(listing.receivingOrganization);
    } else {
      setOrganizationFrom(me?.organizations?.[0]);
    }
  }, [listing, setOrganizationFrom, me]);

  // Helpers
  const resetState = () => {
    setStep(ShareStep.INITIAL_STEP);
    setEmail('');
    setOrganization(undefined);
  };

  // Handlers
  const handleOnGoBack = () => {
    if (step === ShareStep.SHARE_WITH_EMAIL || step === ShareStep.SHARE_WITH_ORG_STEP_1) {
      resetState();
    } else if (step === ShareStep.SHARE_WITH_ORG_STEP_2) {
      setStep(ShareStep.SHARE_WITH_ORG_STEP_1);
      setOrganization(undefined);
    } else {
      handleOnClose();
    }
  };

  const handleOnClose = () => {
    setIsOpen(false);
    // Add a small delay to allow the modal to close before resetting the values.
    setTimeout(() => resetState(), CLOSE_MODAL_TIMEOUT);
  };

  const handleOnSubmit = async () => {
    switch (step) {
      case ShareStep.SHARE_WITH_EMAIL: {
        const emailTrimmed = email.trim();
        if (!email.length || !EmailRegex.test(emailTrimmed)) return showError(t('vdp.shareModal.invalidEmail'));
        await shareListingMutation({
          variables: {
            input: {
              listingId: listing.listingId,
              receivingUserEmail: emailTrimmed,
              sharingOrganizationId: organizationFrom?.id,
            },
          },
          refetchQueries: [shouldRefetchListingDetails ? ListingDocument : null],
          update(cache, { data }) {
            if (!listing.receivingOrganization) {
              const sharingOrganization = data.shareListing.sharingOrganization;

              const existingListings: any = cache.readQuery({
                query: ListingsDocument,
                variables: getFiltersQueryVariables(),
              });

              const updatedListings = existingListings?.listings?.nodes?.map((listing) =>
                listing.id === listing.listingId
                  ? {
                      ...listing,
                      receivingOrganization: {
                        ...sharingOrganization,
                      },
                    }
                  : listing,
              );

              cache.writeQuery({
                query: ListingsDocument,
                data: {
                  listings: {
                    nodes: updatedListings,
                  },
                },
              });
            }
          },
        });
        showAlert(t('vdp.shareModal.successToast'));
        break;
      }
      case ShareStep.SHARE_WITH_ORG_STEP_2: {
        if (!organization) return showError(t('vdp.shareModal.noOrganizationSelected'));
        await shareListingMutation({
          variables: {
            input: {
              listingId: listing.listingId,
              receivingOrganizationId: organization?.id,
              sharingOrganizationId: organizationFrom?.id,
            },
          },
          refetchQueries: [shouldRefetchListingDetails ? ListingDocument : null],
        });
        showAlert(t('vdp.shareModal.successToast'));
        break;
      }
    }
    handleOnClose();
  };

  const Footer = () => (
    <StyledFooter>
      <CancelButton onPress={handleOnClose}>{t('vdp.cancel')}</CancelButton>
      <ShareButton onPress={() => void handleOnSubmit()} disabled={disableSubmitButton} loading={loading}>
        {t('vdp.share')}
      </ShareButton>
    </StyledFooter>
  );

  return (
    <Modal
      open={isOpen}
      onGoBack={
        step === (ShareStep.INITIAL_STEP || ShareStep.SHARE_WITH_ORG_ADD_CONNECTION) ? undefined : handleOnGoBack
      }
      hideCloseButton={step !== (ShareStep.INITIAL_STEP || ShareStep.SHARE_WITH_ORG_ADD_CONNECTION)}
      onClose={handleOnClose}
      title={renderTitle()}
      hideFooter
      submitButtonTitle={t('vdp.share')}
      disabledSubmitButton={disableSubmitButton}
      noPadding
      loading={loading}
      width={step === ShareStep.INITIAL_STEP ? '33.563rem' : '25rem'}
      bottomSheetChangeBreakpoint={BottomSheetChangeBreakpoint.Tablet}
    >
      {step === ShareStep.INITIAL_STEP && <InitialStep setStep={setStep} listing={listing} />}
      {isShareByOrgStep && (
        <ShareByOrganizationStep
          listing={listing}
          organization={organization}
          setOrganization={setOrganization}
          organizationFrom={organizationFrom}
          setOrganizationFrom={setOrganizationFrom}
          step={step}
          setStep={setStep}
        />
      )}
      {step === ShareStep.SHARE_WITH_EMAIL && (
        <ShareWithEmailStep
          listing={listing}
          email={email}
          setEmail={setEmail}
          organizationFrom={organizationFrom}
          setOrganizationFrom={setOrganizationFrom}
        />
      )}
      {showFooterButtons && <Footer />}
    </Modal>
  );
};

export default ShareDialog;
